import { SilverbackPageContext } from '@amazeelabs/gatsby-source-silverback';
import {
  ContentRoute,
  createMapper,
  RouteInput,
  RouteSlotInput,
} from '@caritas/ui';

import {
  mapStatusMessages,
  StatusMessagesType,
} from '../utils/status-messages';
import { mapAccordion } from './blocks/Accordion.mappers';
import { mapBackgroundStory } from './blocks/BackgroundStory.mappers';
import { mapBookingSummary } from './blocks/BookingSummaryBlock.mappers';
import { mapBookingSurvey } from './blocks/BookingSurveyBlock.mappers';
import { mapCBox } from './blocks/CBox.mappers';
import { mapCollageStory } from './blocks/CollageStory.mappers';
import { mapContactBoxBlock } from './blocks/ContactBox.mappers';
import { mapContentHub } from './blocks/ContentHub.mappers';
import {
  hasImage,
  mapContentPageHeader,
  mapHeaderImageCopyright,
} from './blocks/ContentPageHeader.mappers';
import { mapConvertFlowBlock } from './blocks/ConvertFlowBlock.mappers';
import { mapDonationWidget } from './blocks/DonationWidget.mappers';
import { mapDynamicTeaserList } from './blocks/DynamicTeaserList.mappers';
import { mapFactSheet } from './blocks/FactSheet.mappers';
import { mapFileList } from './blocks/FileList.mappers';
import { mapFormEmbedBlock } from './blocks/FormEmbed.mappers';
import { mapHtmlBlock } from './blocks/HtmlBlock.mappers';
import { mapIframeBlock } from './blocks/Iframe.mappers';
import { mapImageBlock } from './blocks/Image.mappers';
import { mapImageCarouselBlock } from './blocks/ImageCarousel.mappers';
import { mapImageStory } from './blocks/ImageStory.mappers';
import { mapImpactNumbersBlock } from './blocks/ImpactNumbers.mappers';
import { mapLocationSearchBlock } from './blocks/LocationSearchBlock.mappers';
import { mapLoginRegisterBlock } from './blocks/LoginRegisterBlock.mappers';
import { mapMapBlock } from './blocks/MapBlock.mappers';
import { mapMyProfileBlock } from './blocks/MyProfileBlock.mappers';
import { mapMyProfileEditBlock } from './blocks/MyProfileEditBlock.mappers';
import { mapPetitionTool } from './blocks/Petition.mappers';
import { mapQuote } from './blocks/Quote.mappers';
import { mapSiteSearchBlock } from './blocks/SiteSearchBlock.mappers';
import { mapTeaserList } from './blocks/TeaserList.mappers';
import { mapThumbnailGalleryBlock } from './blocks/ThumbnailGallery.mappers';

const mapContentPageContent = (
  input: Exclude<ContentPageFragment['content'], undefined>,
  pageContext: SilverbackPageContext,
  contentPageInput: ContentPageFragment,
) => {
  const contentMapper = createMapper<
    Array<ContentPageContentFragment>,
    RouteSlotInput<typeof ContentRoute, 'body'>
  >({
    BlockHtmlParagraph: mapHtmlBlock,
    BlockHtmlTable: mapHtmlBlock,
    BlockHtmlList: mapHtmlBlock,
    BlockAccordion: mapAccordion,
    BlockQuote: mapQuote,
    BlockImage: mapImageBlock,
    BlockImageCarousel: mapImageCarouselBlock,
    BlockCBox: mapCBox,
    BlockTeaserList: mapTeaserList,
    BlockFileList: mapFileList,
    BlockMap: mapMapBlock,
    BlockBackgroundStory: mapBackgroundStory,
    BlockImageStory: mapImageStory,
    BlockCollageStory: mapCollageStory,
    BlockContactBox: mapContactBoxBlock,
    BlockForm: mapFormEmbedBlock(pageContext),
    BlockDynamicTeaserList: mapDynamicTeaserList(pageContext),
    BlockContentHub: mapContentHub(pageContext),
    BlockLocationSearch: mapLocationSearchBlock(pageContext),
    BlockIframe: mapIframeBlock,
    BlockLoginRegister: mapLoginRegisterBlock(pageContext),
    BlockFactSheet: mapFactSheet,
    BlockBookingSummary: mapBookingSummary(pageContext),
    BlockBookingSurvey: mapBookingSurvey(pageContext),
    BlockMyProfile: mapMyProfileBlock(pageContext),
    BlockMyProfileEdit: mapMyProfileEditBlock(pageContext),
    BlockDonationWidget: mapDonationWidget(
      contentPageInput.donationPurpose,
      contentPageInput.donationDescription,
    ),
    BlockThumbnailGallery: mapThumbnailGalleryBlock,
    BlockSiteSearch: mapSiteSearchBlock,
    BlockPetition: mapPetitionTool,
    BlockConvertFlow: mapConvertFlowBlock,
    BlockImpactNumbers: mapImpactNumbersBlock,
  });
  return contentMapper(input);
};

export function mapContentPage(
  input: ContentPageFragment,
  pageContext: SilverbackPageContext,
  path: string,
  statusMessages?: StatusMessagesType,
): RouteInput<typeof ContentRoute> {
  return {
    body: [
      mapContentPageHeader(
        input.header,
        pageContext,
        path,
        input.date,
        input.donationPurpose,
        input.donationDescription,
        input.format,
      ),
      ...(statusMessages ? [mapStatusMessages(statusMessages)] : []),
      ...(input.content
        ? mapContentPageContent(input.content, pageContext, input)
        : []),
      ...(hasImage(input.header.media)
        ? [mapHeaderImageCopyright(input.header)]
        : []),
    ],
  };
}
